<template>
  <v-col>
    <s-crud @rowSelected="$emit('rowSelected', $event)" @doubleClick="$emit('rowSelected', [$event])"
      title="Historias Médicas" :filter="filter" :config="config" ref="crudHistory" searchInput>
      <template v-slot:filter>
        <div>
          <v-col style="">
            <v-row justify="center">
              <v-col lg="3">
                <s-select-definition label="Según Ficha General" v-model="filter.MevStatus" :def="1044"
                  clearable></s-select-definition>
              </v-col>
              <v-col lg="3">
                <!-- fecha -->
                <s-date v-model="filter.MevDate" clearable label="Fecha de Examen"></s-date>
              </v-col>
            </v-row>
          </v-col>
        </div>
      </template>
      <template v-slot:MevStatus="{ row }">
        <div>
          <v-chip :dark="true" x-small :color="row.MevStatusColor">
            {{ row.MevStatusName }}
          </v-chip>
        </div>
      </template>
      <template v-slot:Actions="{ row }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="openDiagnosisTimeline(row)">
              <v-icon color="indigo">mdi-timeline-text-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver línea de tiempo de diagnósticos (5 años)</span>
        </v-tooltip>
      </template>
    </s-crud>

    <v-dialog v-model="timelineDialog" max-width="720px" scrollable>
      <v-card flat style="border-radius: 12px; overflow: hidden">
        <!-- Header compacto -->
        <div style="
            background: #3730a3;
            padding: 14px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          ">
          <div>
            <div style="
                font-size: 13px;
                font-weight: 500;
                color: #fff;
                letter-spacing: 0.02em;
              ">
              Línea de tiempo de diagnósticos
            </div>
            <div style="
                font-size: 11px;
                color: rgba(255, 255, 255, 0.65);
                margin-top: 2px;
              ">
              {{ timelineWorkerName || "Trabajador" }}
            </div>
          </div>
          <v-btn icon small @click="timelineDialog = false" style="background: rgba(255, 255, 255, 0.12)">
            <v-icon small color="white">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text style="
            max-height: 65vh;
            padding: 16px 20px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          ">
          <v-progress-linear v-if="timelineLoading" indeterminate color="indigo" class="mb-2" />

          <v-alert v-if="!timelineLoading && timelineItems.length === 0" type="info" text dense>
            No se encontraron diagnósticos en los últimos 5 años.
          </v-alert>

          <!-- Entradas de timeline -->
          <template v-if="!timelineLoading && timelineItems.length > 0">
            <div v-for="(item, idx) in timelineItems" :key="`${item.MevDate}-${idx}`" style="
                display: grid;
                grid-template-columns: 90px 1fr;
                border: 0.5px solid rgba(0, 0, 0, 0.1);
                border-radius: 8px;
                overflow: hidden;
              ">
              <!-- Columna de fecha -->
              <div style="
                  background: #f8f8f7;
                  padding: 14px 12px;
                  border-right: 0.5px solid rgba(0, 0, 0, 0.1);
                  display: flex;
                  flex-direction: column;
                  justify-content: space-between;
                ">
                <div>
                  <div style="
                      font-size: 22px;
                      font-weight: 500;
                      color: #1a1a1a;
                      line-height: 1.1;
                    ">
                    {{ $fun.formatDateView(item.MevDate).split("/")[0] }}
                  </div>
                  <div style="
                      font-size: 10px;
                      color: #888;
                      letter-spacing: 0.08em;
                      text-transform: uppercase;
                      margin-top: 2px;
                    ">
                    {{ getMonthLabel(item.MevDate) }}
                  </div>
                </div>
                <div style="font-size: 10px; color: #aaa; margin-top: 12px">
                  {{ item.Diagnoses.length }} dx
                </div>
              </div>

              <!-- Columna de contenido -->
              <div style="
                  padding: 12px 14px;
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                ">
                <!-- Tags de fuente y aptitud -->
                <div style="
                    display: flex;
                    flex-wrap: wrap;
                    gap: 4px;
                    align-items: center;
                  ">
                  <span v-for="(src, i) in item.Sources" :key="`src-${i}`" :style="getSourceStyle(src)" style="
                      font-size: 10px;
                      font-weight: 500;
                      padding: 2px 8px;
                      border-radius: 4px;
                      letter-spacing: 0.04em;
                    ">{{ src }}</span>
                  <span v-for="(st, i) in item.Statuses" :key="`st-${i}`" :style="getStatusStyle(st)" style="
                      font-size: 10px;
                      font-weight: 500;
                      padding: 2px 8px;
                      border-radius: 4px;
                      letter-spacing: 0.04em;
                    ">{{ st }}</span>
                </div>

                <div style="height: 0.5px; background: rgba(0, 0, 0, 0.08)"></div>

                <!-- Doctor -->
                <div v-if="item.Doctors.length > 0" style="display: flex; align-items: flex-start; gap: 6px">
                  <span style="
                      font-size: 10px;
                      color: #aaa;
                      letter-spacing: 0.05em;
                      text-transform: uppercase;
                      min-width: 52px;
                      padding-top: 2px;
                    ">Doctor</span>
                  <span v-for="(doc, i) in item.Doctors" :key="`doc-${i}`" style="
                      font-size: 11px;
                      color: #555;
                      border: 0.5px solid rgba(0, 0, 0, 0.12);
                      border-radius: 4px;
                      padding: 2px 8px;
                    ">{{ doc }}</span>
                </div>

                <!-- Diagnósticos -->
                <div style="display: flex; align-items: flex-start; gap: 6px">
                  <span style="
                      font-size: 10px;
                      color: #aaa;
                      letter-spacing: 0.05em;
                      text-transform: uppercase;
                      min-width: 52px;
                      padding-top: 2px;
                    ">Dx</span>
                  <div style="display: flex; flex-direction: column; gap: 2px">
                    <div v-for="(diag, i) in item.Diagnoses" :key="`diag-${i}`" style="
                        font-size: 12px;
                        color: #555;
                        display: flex;
                        align-items: center;
                        gap: 5px;
                      ">
                      <span style="
                          width: 3px;
                          height: 3px;
                          border-radius: 50%;
                          background: #bbb;
                          flex-shrink: 0;
                          display: inline-block;
                        "></span>
                      {{ diag }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import s_HistoryRecord from "../../../services/HumanResource/HistoryRecordService";

export default {
  name: "HistoryRecord",
  components: {},
  data() {
    return {
      filter: {
        MevStatus: 0,
        MevDate: this.$moment().format("YYYY-MM-DD"),
      },
      timelineDialog: false,
      timelineLoading: false,
      timelineItems: [],
      timelineWorkerName: "",
      config: {
        model: { NtpID: "ID", MevStatus: "", MevDate: "date" },
        headers: [
          // { text: "ID", value: "NtpID" },
          { text: "Doc", value: "PrsDocumentNumber" },
          { text: "Nombre Completo", value: "NtpFullName" },
          { text: "Edad", value: "NtpAge" },
          { text: "Género", value: "TypeSexText" },
          { text: "Puesto", value: "PstName" },
          { text: "Telefono", value: "NtpTelephone" },
          { text: "Fecha", value: "MevDate" },
          { text: "H. Atencion", value: "MevTime" },
          { text: "Timeline", value: "Actions", sortable: false, width: 90 },
        ],
        service: s_HistoryRecord,
      },
    };
  },
  methods: {
    refreshPagination() {
      const crud = this.$refs.crudHistory;
      if (!crud) return;

      // Best option for this project: SCrud has `refresh(obj)` which reloads when obj is null
      // and keeps current `filter` and `options` (page, sort, etc.).
      try {
        if (typeof crud.refresh === "function") {
          return crud.refresh(null);
        }
      } catch (e) {
        // ignore
      }

      // Fallbacks (depending on older/newer builds)
      try {
        if (typeof crud.getDataFromApi === "function")
          return crud.getDataFromApi();
        if (typeof crud.reload === "function") return crud.reload();
        if (typeof crud.getData === "function") return crud.getData();
        if (typeof crud.load === "function") return crud.load();
        if (typeof crud.search === "function") return crud.search();
      } catch (e) {
        // ignore
      }
    },
    splitDiagnoses(text) {
      if (!text || typeof text !== "string") return [];

      return text
        .split(/\|+/)
        .map((x) => x.trim())
        .filter((x) => x.length > 0);
    },
    mergeTimelineRows(rows) {
      const byDate = new Map();

      rows.forEach((row) => {
        if (!row || !row.MevDate) return;

        const key = this.$moment(row.MevDate).format("YYYY-MM-DD");
        if (!byDate.has(key)) {
          byDate.set(key, {
            MevDate: key,
            Sources: [],
            Statuses: [],
            Doctors: [],
            Diagnoses: [],
            _diagSet: new Set(),
            _statusSet: new Set(),
            _doctorSet: new Set(),
          });
        }

        const item = byDate.get(key);

        if (row.SourceType && !item.Sources.includes(row.SourceType)) {
          item.Sources.push(row.SourceType);
        }

        const status = (row.MevStatusName || "").trim();
        if (status) {
          const normalizedStatus = status.toLowerCase();
          if (!item._statusSet.has(normalizedStatus)) {
            item._statusSet.add(normalizedStatus);
            item.Statuses.push(status);
          }
        }

        const doctor = (row.DoctorName || "").trim();
        if (doctor) {
          const normalizedDoctor = doctor.toLowerCase();
          if (!item._doctorSet.has(normalizedDoctor)) {
            item._doctorSet.add(normalizedDoctor);
            item.Doctors.push(doctor);
          }
        }

        const split = this.splitDiagnoses(
          row.DiagnosisRaw || row.MevDescriptionDiagnosis || ""
        );
        split.forEach((diag) => {
          const normalized = diag.toLowerCase();
          if (!item._diagSet.has(normalized)) {
            item._diagSet.add(normalized);
            item.Diagnoses.push(diag);
          }
        });
      });

      return Array.from(byDate.values())
        .map((item) => {
          const output = { ...item };
          delete output._diagSet;
          delete output._statusSet;
          delete output._doctorSet;
          return output;
        })
        .sort(
          (a, b) =>
            this.$moment(b.MevDate).valueOf() -
            this.$moment(a.MevDate).valueOf()
        );
    },
    async openDiagnosisTimeline(row) {
      if (!row || !row.PrsDocumentNumber) {
        this.$fun.alert("No se encontró el DNI del trabajador", "warning");
        return;
      }

      this.timelineDialog = true;
      this.timelineLoading = true;
      this.timelineItems = [];
      this.timelineWorkerName = row.NtpFullName || "";

      try {
        const response = await s_HistoryRecord.getDiagnosisTimeline(
          row.PrsDocumentNumber,
          5
        );
        const rows = Array.isArray(response?.data) ? response.data : [];
        this.timelineItems = this.mergeTimelineRows(rows);
      } catch (error) {
        this.$fun.alert(
          "No se pudo cargar la línea de tiempo de diagnósticos",
          "error"
        );
      } finally {
        this.timelineLoading = false;
      }
    },
    getMonthLabel(dateStr) {
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const parts = dateStr.split('/'); // ajusta según tu formato
      const d = new Date(dateStr);
      return `${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    getSourceStyle(src) {
      const map = {
        'HISTORIA ACTIVA': 'background:#ddd6fe;color:#4338ca',
        'MASIVO': 'background:#d1fae5;color:#065f46',
      };
      return map[src] || 'background:#f1f0eb;color:#555';
    },
    getStatusStyle(st) {
      const map = {
        'APTO': 'background:#dcfce7;color:#166534',
        'APTO C/R': 'background:#e0e7ff;color:#3730a3',
        'NO APTO': 'background:#fee2e2;color:#991b1b',
      };
      return map[st] || 'background:#f1f0eb;color:#555';
    },
  },
};
</script>
